// Module 3 — Well Profile schema (v1).
//
// The Well Profile is the MASTER record of RigSentinel. Every later module
// (BHA, Drill String, Mud Program, Cement Program, Kill Sheets, Daily Reports,
// Operations) will attach itself to a Well Profile via the `children` map.
//
// Philosophy:
//   * Enter once. Reuse everywhere.
//   * References (not copies) of Reference-Library records via *RefId fields.
//   * `children` is a real container map — empty arrays in pass 1, populated
//     by future Module 3.x passes. No data duplication.
//
// Storage key: rsc.wells.v1  (see ./storage.js).

export const WELL_SCHEMA_VERSION = 1;

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------
export const WELL_TYPES = [
  "exploration",
  "appraisal",
  "development",
  "injector",
  "observation",
  "other",
];

export const WELL_PURPOSES = [
  "producer",
  "injector",
  "observation",
  "abandonment",
  "other",
];

// Optional operational classification — used for reporting only. May be blank.
export const WELL_OBJECTIVES = [
  "",                // explicit "none / not set"
  "wildcat",
  "development",
  "step-out",
  "infill",
  "sidetrack",
  "relief-well",
  "re-entry",
  "pilot",
  "observation",
  "other",
];

export const ENVIRONMENTS = ["onshore", "offshore"];
export const BOP_LOCATIONS = ["surface", "subsea"];

export const WELL_STATUSES = [
  "planned",
  "drilling",
  "suspended",
  "completed",
  "abandoned",
];

export const COORD_REF_SYSTEMS = ["WGS84", "NAD27", "NAD83", "ED50", "Other"];

// A well belongs to Subsea mode only when it's genuinely offshore with a
// subsea BOP; everything else (onshore, or offshore with a surface BOP —
// e.g. a jack-up) is a Surface-mode well. Single canonical source for this
// rule — WellProfileHub, SessionSwitcher and WellProfileEditor each already
// had their own inline copy; well-scoped downstream pages (Kill Sheet, BHA,
// Drill String, Mud Program hubs/editors) import this one so the rule can
// never drift between call sites.
export function wellStackMode(w) {
  return (w?.environment === "offshore" && w?.bopLocation === "subsea") ? "subsea" : "surface";
}

// Hole-section labels for the Casing Program subtable. These are conventions —
// `holeSection` is ultimately a free-text field so unusual well designs work too.
export const HOLE_SECTIONS = [
  "Conductor",
  "Surface",
  "Intermediate",
  "Production",
  "Liner",
  "Tieback",
  "Other",
];

// ---------------------------------------------------------------------------
// Empty / default factories
// ---------------------------------------------------------------------------

/** Empty Casing Program row. References a Casing Library record by id. */
export function emptyCasingRow() {
  return {
    id: "",                        // assigned by editor (csg_NN)
    holeSection: "Surface",        // free-text but defaulted to a common one
    topDepthFt: "",
    bottomDepthFt: "",
    casingRefId: "",               // → reflibs.casing record (OPTIONAL)
    // Manual dimension overrides — entered by the operator, kept strictly
    // separate from the denormalised library copies below. These ALWAYS win
    // over the linked library record (source precedence: manual → library →
    // missing). A library link is optional when these are supplied.
    manualInnerDiameterIn: "",     // casing drift / internal diameter, in
    manualOuterDiameterIn: "",     // actual casing OD, in (not nominal size)
    // Denormalised display copies (populated when a library record is picked).
    // These are NEVER edited directly — they're refreshed from the library.
    nominalSize: "",
    weightPpf: "",
    grade: "",
    connection: "",
    notes: "",
  };
}

/**
 * Empty Well Profile.
 *
 * `children` is a forward-compatible map of child-record arrays. Future
 * Module 3.x passes will populate these — kept here from day one so storage
 * payloads never have to migrate to add a new container.
 */
export function emptyWellProfile() {
  return {
    id: "",
    schemaVersion: WELL_SCHEMA_VERSION,

    // Identity
    wellName: "",
    uwi: "",
    operator: "",
    client: "",
    field: "",
    block: "",
    country: "",
    rigName: "",
    rigContractor: "",

    // RigSentinel Context Engine — optional Project Profile binding.
    // Populated by the explicit Migration Wizard (lib/rce/migration.js) or
    // by future editors. Never auto-migrated.
    projectId: "",

    // Classification
    wellType: "exploration",
    wellPurpose: "producer",
    wellObjective: "",              // optional — see WELL_OBJECTIVES
    environment: "onshore",
    bopLocation: "surface",         // only meaningful when environment=offshore

    // Depths & datums (all in ft)
    waterDepthFt: "",
    airGapFt: "",
    rkbElevationFt: "",
    rtToGroundFt: "",
    groundElevationFt: "",
    totalDepthPlannedFt: "",
    totalDepthCurrentFt: "",
    totalDepthFinalFt: "",

    // Open-hole (drilled) diameter below the casing shoe, in inches.
    // MANUAL override with the highest precedence for every open-hole
    // annular calculation; falls back to the bound BHA's bit size. Nominal
    // casing size is never used as a substitute.
    openHoleDiameterIn: "",

    // Surface location
    latitude: "",
    longitude: "",
    coordRefSystem: "WGS84",

    // Status & timeline
    status: "planned",
    spudDate: "",
    releaseDate: "",

    // Leak-Off / Formation Integrity Test record (RCE-4.5 — optional).
    // Owned here so the Kill Sheet can inherit LOT pressure + MW@LOT
    // for the MAASP calculation without the user re-entering them.
    lotPressurePsi: "",
    mwAtLotPpg: "",

    // RC-1 — Active operational configuration. Owned by the Well, mirroring
    // the existing Active Mud Program pattern (lib/mud/program.js).
    // The first BHA / Drill String created for a Well becomes active
    // automatically; subsequent creations DO NOT replace active silently.
    // SessionSwitcher remains an OPTIONAL per-user override that wins over
    // these values when set; otherwise the resolver inherits from here.
    activeBhaId: "",
    activeDrillStringId: "",

    // Module 2 references
    bopRefId: "",                   // → reflibs.bop record id

    // Child containers — populated by future Module 3.x passes.
    children: {
      casingProgram: [],            // Implemented in pass 1
      bha: [],
      drillString: [],
      mudPrograms: [],
      cementPrograms: [],
      killSheets: [],
      dailyReports: [],
      operations: [],
    },

    notes: "",
    isActive: true,

    // Save vs. Complete correction (Support correction — Aug 2026, follow-up).
    // Which required sections have had an explicit Save since their last
    // genuine edit. Needed because Classification/Status ship with sane
    // non-empty defaults (wellType, wellPurpose, environment, bopLocation,
    // status="planned") that would otherwise satisfy a plain "has data"
    // check before the operator ever touches the section. Purely additive
    // UI bookkeeping — does not affect any stored well-control value.
    sectionsTouched: {},

    createdAt: null,
    updatedAt: null,
  };
}

/**
 * Migrate a legacy persisted well so it loads cleanly into the v1 editor.
 *
 * Currently handles:
 *   * `rig` (legacy single field) → splits into `rigName` / `rigContractor`
 *     when those new fields are missing or empty.
 *   * Missing `wellObjective` defaults to "" (no objective set).
 *   * Missing child containers are restored from the empty profile.
 *
 * Pure function — does NOT touch storage. Safe to call on any input.
 */
export function migrateLegacyWellProfile(raw) {
  if (!raw || typeof raw !== "object") return raw;
  const base = emptyWellProfile();
  const out = { ...base, ...raw };

  // Rig split — only when the new fields are empty AND a legacy `rig` exists.
  if ((!out.rigName || !String(out.rigName).trim()) && raw.rig) {
    out.rigName = raw.rig;
  }
  // `rig` is no longer part of the canonical model — drop it to avoid drift.
  if ("rig" in out) delete out.rig;

  // Ensure every child container exists.
  out.children = { ...base.children, ...(raw.children || {}) };

  // Data-consistency normalization (Master Correction Pass, Defect 6) — a
  // subsea BOP stack is inherently offshore. Corrects wells saved before
  // this rule existed where `bopLocation` was set to "subsea" while
  // `environment` was left at the default "onshore" (the two fields were
  // previously editable independently). Never touches "surface" bopLocation
  // — that says nothing about onshore vs. offshore on its own.
  if (out.bopLocation === "subsea" && out.environment !== "offshore") {
    out.environment = "offshore";
  }

  return out;
}


// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

const _isPosNum = (v) => v !== "" && v != null && Number.isFinite(Number(v)) && Number(v) > 0;
const _isNonNegNum = (v) => v !== "" && v != null && Number.isFinite(Number(v)) && Number(v) >= 0;
const _optPosNum = (v) => v === "" || v == null || _isPosNum(v);
const _optNonNegNum = (v) => v === "" || v == null || _isNonNegNum(v);

function _isIsoDateOrEmpty(v) {
  if (v === "" || v == null) return true;
  // Accept YYYY-MM-DD (HTML date input) or full ISO timestamps.
  return /^\d{4}-\d{2}-\d{2}/.test(String(v));
}

function _validateCasingRow(row, idx) {
  const errs = [];
  const tag = `Casing row ${idx + 1}`;
  if (!_optNonNegNum(row.topDepthFt))     errs.push(`${tag}: top depth must be ≥ 0 if entered.`);
  if (!_optPosNum(row.bottomDepthFt))     errs.push(`${tag}: bottom depth must be > 0 if entered.`);
  if (_isNonNegNum(row.topDepthFt) && _isPosNum(row.bottomDepthFt) &&
      Number(row.topDepthFt) >= Number(row.bottomDepthFt))
    errs.push(`${tag}: top depth must be less than bottom depth.`);
  // Manual dimension overrides — an invalid override is an error, never a
  // silent fall-back to the linked library record.
  if (!_optPosNum(row.manualInnerDiameterIn)) errs.push(`${tag}: casing ID must be > 0 if entered.`);
  if (!_optPosNum(row.manualOuterDiameterIn)) errs.push(`${tag}: casing OD must be > 0 if entered.`);
  if (_isPosNum(row.manualInnerDiameterIn) && _isPosNum(row.manualOuterDiameterIn) &&
      Number(row.manualOuterDiameterIn) <= Number(row.manualInnerDiameterIn))
    errs.push(`${tag}: casing OD must be greater than casing ID.`);
  // casingRefId is not required — users may add a row before picking a library record.
  return errs;
}

/**
 * Validate a Well Profile.
 *
 * Pure function — does NOT check uniqueness of `uwi` across other wells
 * (that's enforced at the storage layer where we can see siblings).
 */
export function validateWellProfile(rec) {
  if (!rec || typeof rec !== "object")
    return { ok: false, errors: ["Well profile is missing."] };

  const errs = [];

  // Identity ---------------------------------------------------------------
  if (!rec.wellName || !String(rec.wellName).trim())
    errs.push("Well name is required.");

  // Classification --------------------------------------------------------
  if (!WELL_TYPES.includes(rec.wellType))
    errs.push(`Well type must be one of: ${WELL_TYPES.join(", ")}.`);
  if (!WELL_PURPOSES.includes(rec.wellPurpose))
    errs.push(`Well purpose must be one of: ${WELL_PURPOSES.join(", ")}.`);
  if (rec.wellObjective != null && rec.wellObjective !== "" &&
      !WELL_OBJECTIVES.includes(rec.wellObjective))
    errs.push(`Well objective must be one of: ${WELL_OBJECTIVES.filter(Boolean).join(", ")}.`);
  if (!ENVIRONMENTS.includes(rec.environment))
    errs.push(`Environment must be either onshore or offshore.`);
  if (rec.environment === "offshore") {
    if (!BOP_LOCATIONS.includes(rec.bopLocation))
      errs.push("Offshore wells must have BOP location set to surface or subsea.");
    if (!_isPosNum(rec.waterDepthFt))
      errs.push("Offshore wells must have a positive water depth (ft).");
  }

  // Depths & datums -------------------------------------------------------
  const depthChecks = [
    ["airGapFt", "Air gap"],
    ["rkbElevationFt", "RKB elevation"],
    ["rtToGroundFt", "RT-to-ground"],
    ["groundElevationFt", "Ground elevation"],
    ["totalDepthPlannedFt", "Total depth (planned)"],
    ["totalDepthCurrentFt", "Total depth (current)"],
    ["totalDepthFinalFt", "Total depth (final)"],
    ["waterDepthFt", "Water depth"],
  ];
  for (const [field, label] of depthChecks) {
    if (rec[field] !== "" && rec[field] != null &&
        (!Number.isFinite(Number(rec[field])) || Number(rec[field]) < 0))
      errs.push(`${label} must be a non-negative number if entered.`);
  }
  if (_isPosNum(rec.totalDepthCurrentFt) && _isPosNum(rec.totalDepthFinalFt) &&
      Number(rec.totalDepthCurrentFt) > Number(rec.totalDepthFinalFt))
    errs.push("Total depth (current) cannot exceed total depth (final).");

  // Formation Strength Test (LOT) — optional, but must be positive if
  // entered (unlike the depth fields above, 0 is not a physically valid
  // pressure/density here).
  if (!_optPosNum(rec.lotPressurePsi))
    errs.push("LOT pressure must be a positive number if entered.");
  if (!_optPosNum(rec.mwAtLotPpg))
    errs.push("MW at LOT must be a positive number if entered.");

  // Open-hole diameter — manual override, must be usable if entered (an
  // invalid override never falls back to the BHA bit size silently).
  if (!_optPosNum(rec.openHoleDiameterIn))
    errs.push("Open-hole diameter must be a positive number if entered.");

  // Coordinates -----------------------------------------------------------
  if (rec.latitude !== "" && rec.latitude != null) {
    const lat = Number(rec.latitude);
    if (!Number.isFinite(lat) || lat < -90 || lat > 90)
      errs.push("Latitude must be a number between -90 and 90.");
  }
  if (rec.longitude !== "" && rec.longitude != null) {
    const lon = Number(rec.longitude);
    if (!Number.isFinite(lon) || lon < -180 || lon > 180)
      errs.push("Longitude must be a number between -180 and 180.");
  }
  if (!COORD_REF_SYSTEMS.includes(rec.coordRefSystem) && rec.coordRefSystem !== "")
    errs.push(`Coordinate reference system must be one of: ${COORD_REF_SYSTEMS.join(", ")}.`);

  // Status & timeline -----------------------------------------------------
  if (!WELL_STATUSES.includes(rec.status))
    errs.push(`Well status must be one of: ${WELL_STATUSES.join(", ")}.`);
  if (!_isIsoDateOrEmpty(rec.spudDate))    errs.push("Spud date must be a valid date if entered.");
  if (!_isIsoDateOrEmpty(rec.releaseDate)) errs.push("Release date must be a valid date if entered.");
  if (rec.spudDate && rec.releaseDate &&
      String(rec.spudDate) > String(rec.releaseDate))
    errs.push("Spud date must be on or before release date.");

  // Casing program rows ---------------------------------------------------
  const csg = Array.isArray(rec.children?.casingProgram) ? rec.children.casingProgram : [];
  csg.forEach((row, i) => { errs.push(..._validateCasingRow(row, i)); });

  return { ok: errs.length === 0, errors: errs };
}

/**
 * Validate ONE Well Profile section against only its own fields (Support
 * correction, follow-up — ticket 237121). validateWellProfile above is
 * left byte-for-byte unchanged and remains the whole-record "overall
 * readiness" check (used before leaving the page / the final Save). This
 * scoped sibling exists so pressing Save on an individual section only
 * blocks that section's own save on that section's own bad data — a
 * requirement belonging to a later section must never refuse an earlier
 * section's work-in-progress save.
 *
 * Every validateWellProfile rule maps to exactly one section EXCEPT one:
 * "offshore wells must have a positive water depth" — the trigger field
 * (environment) lives in Classification but the value it requires
 * (waterDepthFt) lives in Depths & Datums. That single cross-section rule
 * is intentionally NOT enforced here; it only applies in the whole-record
 * check, i.e. it gates overall readiness, not the Classification save.
 */
export function validateWellProfileSection(rec, sectionKey) {
  if (!rec || typeof rec !== "object")
    return { ok: false, errors: ["Well profile is missing."] };

  const errs = [];

  switch (sectionKey) {
    case "identity":
      if (!rec.wellName || !String(rec.wellName).trim())
        errs.push("Well name is required.");
      break;

    case "classification":
      if (!WELL_TYPES.includes(rec.wellType))
        errs.push(`Well type must be one of: ${WELL_TYPES.join(", ")}.`);
      if (!WELL_PURPOSES.includes(rec.wellPurpose))
        errs.push(`Well purpose must be one of: ${WELL_PURPOSES.join(", ")}.`);
      if (rec.wellObjective != null && rec.wellObjective !== "" &&
          !WELL_OBJECTIVES.includes(rec.wellObjective))
        errs.push(`Well objective must be one of: ${WELL_OBJECTIVES.filter(Boolean).join(", ")}.`);
      if (!ENVIRONMENTS.includes(rec.environment))
        errs.push("Environment must be either onshore or offshore.");
      if (rec.environment === "offshore" && !BOP_LOCATIONS.includes(rec.bopLocation))
        errs.push("Offshore wells must have BOP location set to surface or subsea.");
      // Water-depth-if-offshore is deliberately NOT checked here — see doc comment above.
      break;

    case "depths": {
      const depthChecks = [
        ["airGapFt", "Air gap"],
        ["rkbElevationFt", "RKB elevation"],
        ["rtToGroundFt", "RT-to-ground"],
        ["groundElevationFt", "Ground elevation"],
        ["totalDepthPlannedFt", "Total depth (planned)"],
        ["totalDepthCurrentFt", "Total depth (current)"],
        ["totalDepthFinalFt", "Total depth (final)"],
        ["waterDepthFt", "Water depth"],
      ];
      for (const [field, label] of depthChecks) {
        if (rec[field] !== "" && rec[field] != null &&
            (!Number.isFinite(Number(rec[field])) || Number(rec[field]) < 0))
          errs.push(`${label} must be a non-negative number if entered.`);
      }
      if (_isPosNum(rec.totalDepthCurrentFt) && _isPosNum(rec.totalDepthFinalFt) &&
          Number(rec.totalDepthCurrentFt) > Number(rec.totalDepthFinalFt))
        errs.push("Total depth (current) cannot exceed total depth (final).");
      if (!_optPosNum(rec.lotPressurePsi))
        errs.push("LOT pressure must be a positive number if entered.");
      if (!_optPosNum(rec.mwAtLotPpg))
        errs.push("MW at LOT must be a positive number if entered.");
      if (!_optPosNum(rec.openHoleDiameterIn))
        errs.push("Open-hole diameter must be a positive number if entered.");
      // The offshore-requires-water-depth rule is intentionally enforced only
      // in the whole-record overall-readiness check, not here — saving Depths
      // & Datums itself must not be blocked by Classification's environment.
      break;
    }

    case "location":
      if (rec.latitude !== "" && rec.latitude != null) {
        const lat = Number(rec.latitude);
        if (!Number.isFinite(lat) || lat < -90 || lat > 90)
          errs.push("Latitude must be a number between -90 and 90.");
      }
      if (rec.longitude !== "" && rec.longitude != null) {
        const lon = Number(rec.longitude);
        if (!Number.isFinite(lon) || lon < -180 || lon > 180)
          errs.push("Longitude must be a number between -180 and 180.");
      }
      if (!COORD_REF_SYSTEMS.includes(rec.coordRefSystem) && rec.coordRefSystem !== "")
        errs.push(`Coordinate reference system must be one of: ${COORD_REF_SYSTEMS.join(", ")}.`);
      break;

    case "status":
      if (!WELL_STATUSES.includes(rec.status))
        errs.push(`Well status must be one of: ${WELL_STATUSES.join(", ")}.`);
      if (!_isIsoDateOrEmpty(rec.spudDate))    errs.push("Spud date must be a valid date if entered.");
      if (!_isIsoDateOrEmpty(rec.releaseDate)) errs.push("Release date must be a valid date if entered.");
      if (rec.spudDate && rec.releaseDate &&
          String(rec.spudDate) > String(rec.releaseDate))
        errs.push("Spud date must be on or before release date.");
      break;

    case "casing": {
      const csg = Array.isArray(rec.children?.casingProgram) ? rec.children.casingProgram : [];
      csg.forEach((row, i) => { errs.push(..._validateCasingRow(row, i)); });
      break;
    }

    default:
      // bop, bha, drillstring, mud, killsheet, tubing, notes carry no
      // save-time validation rule of their own today.
      break;
  }

  return { ok: errs.length === 0, errors: errs };
}
